import React, { useCallback, useEffect, useMemo, useState } from "react";
import { connect, useDispatch, useSelector } from "react-redux";
import _ from "lodash";
import userActions from "../../../_actions/userActions";
import unitActions from "../../../_actions/unitActions";
import AlertModal from "../../_common/AlertModal";
import Select from "react-select";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { modalTabIds } from "../../../_constants/modalTabIds";
import employeeActions from "../../../_actions/employeeActions";

export default function AddUserModalV2(props) {
  const {
    showModal,
    toggleModal,
    user,
    acceptCaption,
    tabId,
    submitCallback,
    allowRegisterAsOfficeAdmin,
  } = props;

  const dispatch = useDispatch();

  const userRoles = useSelector((state) => state.users.userRoles || null);
  const units = useSelector(
    (state) => state.users.openUnitLists[modalTabIds.EDIT_USER] || null
  );

  useEffect(() => {
    dispatch(userActions.getUserRoles());
    dispatch(unitActions.getAll(null, modalTabIds.EDIT_USER));
    validateForm();
  }, []);

  const [_userData, setUserData] = useState(user ? { ...user } : {});
  useEffect(() => setUserData(user ? { ...user } : {}), [user]);

  const unitOptions = useMemo(
    () =>
      _.orderBy(
        (units || []).map((u) => ({
          value: u.unitId,
          label: u.distUnit,
          district: u.district,
        })),
        "label",
        "asc"
      ),
    [units]
  );

  const userRoleOptions = useMemo(() => {
    const userRoleOrder = {
      CONSULT: 0,
      TET: 1,
      TEC: 2,
      TEE: 3,
      TEL: 4,
      STE: 5,
      SBE: 6,
      STEE: 7,
      SUPTE: 8,
      SUPBE: 9,
      AGPA: 10,
      SSM: 11,
    };
    return _.orderBy(
      (userRoles || [])
        .filter((ur) => ur.userRoleSymbol !== "OA")
        .map((ur) => ({
          value: ur.userRoleId,
          label: ur.description,
          order: userRoleOrder[ur.userRoleSymbol],
        })),
      "order",
      "asc"
    );
  }, [userRoles]);

  const [showAlert_editUserConfirm, setShowAlert_editUserConfirm] = useState(false);
  const toggleAlert_editUserConfirm = () =>
    setShowAlert_editUserConfirm(!showAlert_editUserConfirm);

  const [firstName, setFirstName] = useState(user ? user.firstName || "" : "");
  const [middleInitial, setMiddleInitial] = useState(
    user ? user.middleInitial || "" : ""
  );
  const [lastName, setLastName] = useState(user ? user.lastName || "" : "");
  const [employeeNo, setEmployeeNo] = useState(user ? user.employeeNo : "");
  const [selectedUserRole, setSelectedUserRole] = useState(
    user ? (userRoleOptions || []).find((uro) => uro.value === user.userRoleId) : null
  );
  const [selectedUnit, setSelectedUnit] = useState(
    user ? (unitOptions || []).find((uo) => uo.value === user.unitId) : null
  );
  const [isDistrictAdmin, setIsDistrictAdmin] = useState(
    user ? user.isOfficeAdmin : false
  );

  const [employeeIsAlreadyRegistered, setEmployeeIsAlreadyRegistered] = useState(false);
  const [employeeDoesNotExist, setEmployeeDoesNotExist] = useState(user ? false : true);

  const employeeNoIsComplete = useMemo(() => {
    const _empNo = (employeeNo || "").toLowerCase();
    if (/(^s)/g.test(_empNo)) return /(^s)\d{6}/g.test(_empNo);
    else if (/(^p)/g.test(_empNo)) return /(^p)\d{5,6}/g.test(_empNo);
    else return false;
  }, [employeeNo]);

  const [formIsIncomplete, setFormIsIncomplete] = useState(false);
  const [formErrors, setFormErrors] = useState({
    firstName: null,
    lastName: null,
    userRole: null,
    employeeNo: null,
  });

  useEffect(
    () => setFormIsIncomplete(Object.values(formErrors).some((e) => e)),
    [formErrors]
  );

  const validateForm = useCallback(() => {
    const standardError = "Required";
    // If employeeNo field is not complete, do not display error until user
    // finishes typing to a complete employeeNo.
    // If employee is already registered, do not display error.
    // If employee is not registered, but user data is available and is populated,
    // do not show error.
    const doNotShowErr = (reqValue) =>
      !employeeNoIsComplete ||
      employeeDoesNotExist ||
      employeeIsAlreadyRegistered ||
      (!employeeIsAlreadyRegistered && reqValue);

    const _formErrors = {
      ...formErrors,
      firstName: doNotShowErr(firstName) ? null : standardError,
      lastName: doNotShowErr(lastName) ? null : standardError,
      userRole: doNotShowErr(selectedUserRole) ? null : standardError,
      employeeNo: employeeNo && employeeNoIsComplete ? null : standardError,
    };
    setFormErrors(_formErrors);
  }, [
    firstName,
    lastName,
    selectedUserRole,
    employeeNo,
    employeeNoIsComplete,
    employeeIsAlreadyRegistered,
    employeeDoesNotExist,
  ]);

  useEffect(() => getEmployeeRegistrationStatus(), [employeeNo]);

  const getEmployeeRegistrationStatus = useCallback(async () => {
    // If employeeNo is complete, try to get employee by employeeNo.
    // If found (whether registered or not), prepopulate name fields.
    if (employeeNoIsComplete) {
      const _employee = await dispatch(employeeActions.get(employeeNo.toLowerCase()));
      const _user = await dispatch(
        userActions.getInfoByEmployeeNo(employeeNo.toLowerCase())
      );

      if (_employee) {
        // Employee exists in the Employees table
        setEmployeeDoesNotExist(false);
        setEmployeeIsAlreadyRegistered(_employee.isRegisteredInProDMS);

        // If employee is found to be registered ProDMS user, then populate based
        // on the user data. Otherwise, use the employee data.
        setFirstName(
          _employee.isRegisteredInProDMS && _user ? _user.firstName : _employee.firstName
        );
        setMiddleInitial(
          _employee.isRegisteredInProDMS && _user
            ? _user.middleInitial
            : _employee.middleInitial
        );
        setLastName(
          _employee.isRegisteredInProDMS && _user ? _user.lastName : _employee.lastName
        );
      } else {
        // Employee is not found. Most likely due to:
        // - Employee left state service
        // - Employee is new hire and Employees table is not yet updated
        setEmployeeDoesNotExist(true);
        if (_user) {
          // Employee was not found in Employees table but listed as a registered ProDMS user
          setFirstName(_user.firstName);
          setMiddleInitial(_user.middleInitial);
          setLastName(_user.lastName);
          setEmployeeIsAlreadyRegistered(true);
        } else {
          // Reset all fields for new user input
          setEmployeeIsAlreadyRegistered(false);
          setFirstName("");
          setMiddleInitial("");
          setLastName("");
          setSelectedUserRole(null);
          setSelectedUnit(null);
        }
      }

      // Whether employee was found or not, if user was found, set userRole and unit
      if (_user) {
        setSelectedUserRole(
          _user.userRoleId
            ? userRoleOptions.find((ur) => ur.value === _user.userRoleId)
            : null
        );
        setSelectedUnit(
          _user.unitId
            ? unitOptions.find((uo) => uo.value === _user.unitId) || null
            : null
        );
      }
    }
    // If employeeNo value does not match required pattern, reinitialize values as below.
    // Until the pattern matches, rest of the form will remain disabled.
    else {
      setEmployeeIsAlreadyRegistered(false);
      setEmployeeDoesNotExist(true);
      setFirstName("");
      setMiddleInitial("");
      setLastName("");
      setSelectedUserRole(null);
      setSelectedUnit(null);
    }
    validateForm();
  }, [employeeNo, employeeNoIsComplete]);

  useEffect(() => validateForm(), [employeeNo, firstName, lastName, selectedUserRole]);

  const handleBlur = (fieldName, value) => {
    validateForm();
    setUserData({ ..._userData, [fieldName]: value });
  };

  const handleChange_userRole = (selectedOption) => {
    setSelectedUserRole(selectedOption);
    setUserData({ ..._userData, userRoleId: selectedOption?.value });
    validateForm();
  };

  const handleChange_unit = (selectedOption) => {
    setSelectedUnit(selectedOption);
    setUserData({ ..._userData, unitId: selectedOption?.value });
  };

  const handleAlert_editUserConfirm = (actionType) => (e) => {
    e.preventDefault();
    if (actionType === "accept") {
      toggleAlert_editUserConfirm();
      toggleModal();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formIsIncomplete) return;

    const userData = {
      userId: user ? user.userId : 0,
      employeeNo,
      firstName,
      middleInitial,
      lastName,
      userRoleId: selectedUserRole?.value,
      unitId: selectedUnit?.value,
    };

    // Create new user by admin, from scratch
    const newUser = await dispatch(userActions.add(userData));
    submitCallback && submitCallback(newUser.userId);
    toggleAlert_editUserConfirm();
  };

  const renderInputs = () => {
    return (
      <div className="card flex-grow-1">
        <div className="card-body">
          <div className="row">
            <div className="form-group col px-2">
              <label className="mb-1" htmlFor="employeeNo">
                <b>Employee No.:</b>* <small>(ex: s999999, p999999)</small>
              </label>
              <input
                type="text"
                className="form-control"
                name="employeeNo"
                id="employeeNo"
                maxLength="7"
                value={employeeNo || ""}
                onChange={(e) => setEmployeeNo(e.target.value)}
                onBlur={(e) => handleBlur(e.target.name, e.target.value)}
                disabled={!!user}
              />
              {formErrors.employeeNo && (
                <span className="error-small">
                  <small>{formErrors.employeeNo}</small>
                </span>
              )}
              {employeeNoIsComplete && employeeDoesNotExist && (
                <div style={{ lineHeight: "1rem" }}>
                  <span className="error-small">
                    <small>
                      <b>Employee with this s# or p# does not exist.</b>
                    </small>
                  </span>
                </div>
              )}
              {!user && employeeIsAlreadyRegistered && (
                <div style={{ lineHeight: "1rem" }}>
                  <span className="error-small">
                    <small>This employee is already registered in ProDMS.</small>
                  </span>
                </div>
              )}
            </div>
          </div>
          <div className="row">
            <div className="form-group col-9 px-2">
              <label className="mb-1" htmlFor="firstName">
                <b>First Name:</b>*
              </label>
              <input
                type="text"
                className="form-control"
                name="firstName"
                id="firstName"
                value={firstName || ""}
                onChange={(e) => setFirstName(e.target.value)}
                onBlur={(e) => handleBlur(e.target.name, e.target.value)}
                disabled={
                  !employeeNoIsComplete ||
                  employeeDoesNotExist ||
                  (!user && employeeIsAlreadyRegistered)
                }
              />
              {formErrors.firstName && (
                <span className="error-small">
                  <small>{formErrors.firstName}</small>
                </span>
              )}
            </div>
            <div className={"form-group col-3 px-2"}>
              <label className="mb-1" htmlFor="middleInitial">
                MI:
              </label>
              <input
                type="text"
                className="form-control"
                name="middleInitial"
                id="middleInitial"
                maxLength="1"
                value={middleInitial || ""}
                onChange={(e) => setMiddleInitial(e.target.value)}
                onBlur={(e) => handleBlur(e.target.name, e.target.value)}
                disabled={
                  !employeeNoIsComplete ||
                  employeeDoesNotExist ||
                  (!user && employeeIsAlreadyRegistered)
                }
              />
            </div>
          </div>
          <div className="row">
            <div className="form-group col px-2">
              <label className="mb-1" htmlFor="lastName">
                <b>Last Name:</b>*
              </label>
              <input
                type="text"
                className="form-control"
                name="lastName"
                id="lastName"
                value={lastName || ""}
                onChange={(e) => setLastName(e.target.value)}
                onBlur={(e) => handleBlur(e.target.name, e.target.value)}
                disabled={
                  !employeeNoIsComplete ||
                  employeeDoesNotExist ||
                  (!user && employeeIsAlreadyRegistered)
                }
              />
              {formErrors.lastName && (
                <span className="error-small">
                  <small>{formErrors.lastName}</small>
                </span>
              )}
            </div>
          </div>
          <div className="row">
            <div className="form-group col px-2">
              <label className="mb-1" htmlFor="district">
                <b>Classification:</b>*
              </label>
              <Select
                name="selectedUserRole"
                id="selectedUserRole"
                value={selectedUserRole}
                isSearchable={false}
                options={userRoleOptions}
                onChange={(selectedOption) => handleChange_userRole(selectedOption)}
                isClearable
                placeholder="Select Classification"
                isDisabled={
                  !employeeNoIsComplete ||
                  employeeDoesNotExist ||
                  (!user && employeeIsAlreadyRegistered)
                }
                styles={{
                  // Fixes the overlapping problem of the component
                  menu: (provided) => ({ ...provided, zIndex: 9999 }),
                }}
              />
              {formErrors.userRole && (
                <span className="error-small">
                  <small>{formErrors.userRole}</small>
                </span>
              )}
            </div>
          </div>
          <div className="row">
            <div className="form-group col px-2">
              <label className="mb-1" htmlFor="district">
                District/Unit:
              </label>
              <Select
                name="selectedUnit"
                id="selectedUnit"
                value={selectedUnit}
                options={unitOptions}
                onChange={(selectedOption) => handleChange_unit(selectedOption)}
                isClearable
                isDisabled={
                  !employeeNoIsComplete ||
                  employeeDoesNotExist ||
                  (!user && employeeIsAlreadyRegistered)
                }
                styles={{
                  // Fixes the overlapping problem of the component
                  menu: (provided) => ({ ...provided, zIndex: 9999 }),
                }}
              />
            </div>
          </div>
          {allowRegisterAsOfficeAdmin && (
            <div className="row">
              <div className="form-group col mb-0 px-2">
                <div className="custom-control custom-checkbox">
                  <input
                    type="checkbox"
                    name="isDistrictAdmin"
                    id="isDistrictAdmin"
                    className="custom-control-input"
                    checked={isDistrictAdmin}
                    onChange={(e) => setIsDistrictAdmin(e.target.checked)}
                    disabled={
                      !employeeNoIsComplete ||
                      employeeDoesNotExist ||
                      (!user && employeeIsAlreadyRegistered)
                    }
                  />
                  <label className="custom-control-label mb-1" htmlFor="isDistrictAdmin">
                    Register as District Administrator
                  </label>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const closeBtn = (
    <button className="close" onClick={toggleModal}>
      <FontAwesomeIcon icon="times" className="fa-fw text-white" />
    </button>
  );

  return (
    <React.Fragment>
      <Modal
        isOpen={showModal}
        toggle={toggleModal}
        className={props.className}
        fade={false}
        size="md"
      >
        <ModalHeader className="bg-info text-white" close={closeBtn}>
          {user ? (
            <FontAwesomeIcon icon="edit" className="fa-fw mr-2" />
          ) : (
            <FontAwesomeIcon icon="user-plus" className="fa-fw mr-2" />
          )}
          {!user
            ? "Add New User"
            : user && user.mustRegister
            ? "ProDMS User Registration"
            : "Edit User"}
        </ModalHeader>
        <ModalBody>
          {user && user.mustRegister && (
            <div className="d-flex flex-row w-100">
              <div className="alert alert-info p-2 w-100">
                <FontAwesomeIcon icon="info-circle" className="fa-fw mr-2" />
                Welcome to ProDMS! Before you begin, please provide your civil service
                classification and unit.
              </div>
            </div>
          )}
          {renderInputs()}
        </ModalBody>
        <ModalFooter className="bg-lightgray">
          <div className="form-group mb-0">
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSubmit}
              disabled={
                formIsIncomplete ||
                employeeDoesNotExist ||
                (!user && employeeIsAlreadyRegistered)
              }
            >
              {acceptCaption || (!user ? "Add" : "Save")}
            </button>
            <button
              type="button"
              className="btn btn-secondary ml-2"
              name="cancelEditUser"
              onClick={toggleModal}
            >
              Cancel
            </button>
          </div>
        </ModalFooter>
      </Modal>
      {showAlert_editUserConfirm && (
        <AlertModal
          alertTitleIcon="check"
          alertTitleClass="bg-success text-white"
          alertTitle={"Registration Successful"}
          alertText={`User '${employeeNo}' is now registered in DMS.`}
          acceptCaption={"OK"}
          acceptClass={"primary"}
          provideReject={false}
          handleAlertAction={handleAlert_editUserConfirm}
          showAlertModal={showAlert_editUserConfirm}
          toggleAlertModal={toggleAlert_editUserConfirm}
        />
      )}
    </React.Fragment>
  );
}

const mapStateToProps = (state) => ({
  authentication: state.authentication,
  userRoles: state.users.userRoles,
});
